/**
 * @file indexBindings.js
 * @description Enterprise-grade Post-initialization UI bindings & Cinematic API
 * @module Core/IndexBindings
 * @version 5.1.0
 */

import { on } from '../core/eventBus.js';
import { showToast } from '../utils/helpers.js';
import { log } from '../utils/logger.js';



// [FIX] AbortController для очистки глобальных слушателей
let _ac = new AbortController();

// ══════════════════════════════════════════════════════════════
// CINEMATIC CONTROLLER (Enterprise Singleton)
// ══════════════════════════════════════════════════════════════
class CinematicController {
  constructor() {
    if (CinematicController.instance) return CinematicController.instance;
    this.isActive = false;
    this.isBound = false;
    CinematicController.instance = this;
  }

  get engine() {
    try { return window.MikhmelViewer?._getCinematicRenderer?.(); } 
    catch (e) { return null; }
  }

  toggle() {
    if (!window.MikhmelViewer?.toggleCinematic) {
      showToast('⚠️ Загрузите DICOM (3D) для режима Кинематографический рендеринг', 'warning', 3000);
      return;
    }
    try {
      window.MikhmelViewer.toggleCinematic();
      this.isActive = !this.isActive;
      this._syncUI();
    } catch (e) {
      log.error('[Cinematic] Toggle failed', e);
      showToast(`⚠️ Ошибка Кинематографический рендеринг: ${e.message}`, 'error', 3000);
    }
  }

  setPreset(preset) {
    try {
      window.MikhmelViewer?.setCinematicPreset?.(preset);
      if (preset === 'off') {
        this.isActive = false;
        this._syncUI();
      } else if (!this.isActive) {
        this.toggle();
      }
    } catch (e) { log.warn('[Cinematic] Preset error', e); }
  }

  /** Умная установка параметров с поддержкой разных архитектур рендера */
  setParameter(param, value) {
    const cr = this.engine;
    if (!cr) return;
    try {
      if (typeof cr.setParameter === 'function') cr.setParameter(param, value);
      else if (cr.params) { cr.params[param] = value; cr.update?.(); cr.render?.(); }
      else if (param in cr) { cr[param] = value; cr.update?.(); }
    } catch (e) { log.debug(`[Cinematic] Failed to set ${param}`, e); }
  }

  _syncUI() {
    const updates = [
      { id: 'cine-toggle-btn', text: this.isActive ? '⏸ Выключить' : '▶ Включить', active: this.isActive },
      { id: 'btn-cinematic-toggle', text: this.isActive ? '🎬 Выкл.' : '🎬 Кинематика', active: this.isActive },
      { id: 'btn-cinematic-left', active: this.isActive, style: { borderColor: this.isActive ? '#fa4' : '' } }
    ];

    updates.forEach(({ id, text, active, style }) => {
      const el = document.getElementById(id);
      if (!el) return;
      if (text) el.textContent = text;
      el.classList.toggle('active', active);
      if (style) Object.assign(el.style, style);
    });
  }
}

const cinematic = new CinematicController();

// [11.0.2] Класс MediaCaptureEngine УДАЛЁН — вслед за последними тремя
// экспортами, которые его звали, и ни один из которых не звался снаружи.
//
// Он снимал `viewport-container.querySelector('canvas')` — ПЕРВУЮ канву в
// документе, то есть канву Cornerstone. Кинематика же рисует на своей,
// вставленной следом (cinematicRendering.js:539). Снимок выходил без
// кинематических эффектов и подписывался «Скриншот высокого разрешения
// сохранён» — вывеска, только не над кнопкой, а над результатом.
//
// Захват живёт в sidebarBindings: там правильная канва, водяной знак и
// распаковка ответа рендерера.

// [11.0.2] Класс UIBinder удалён вместе с единственным своим потребителем —
// набором привязок кинематики. Звать его стало неоткуда; оставлять пустой
// связыватель значило бы держать в файле инструмент без работы.
//
// Одна его черта достойна переезда, и она переехала: `dataset.bound` не даёт
// повесить второго слушателя на тот же элемент. Теперь эту защиту ставит
// хелпер `on()` в sidebarBindings — там, где она и была нужна.

// ══════════════════════════════════════════════════════════════
// MAIN INITIALIZATION
// ══════════════════════════════════════════════════════════════
/**
 * [11.0.2] ВТОРОЙ НАБОР ПРИВЯЗОК КИНЕМАТИКИ УДАЛЁН ЦЕЛИКОМ.
 *
 * ПОВОД. У правого сайдбара было два хозяина. `initUI()` (main.js:381) звал
 * sidebarBindings, а `initIndexBindings()` (main.js:548) — этот набор, поверх
 * тех же самых элементов. Защита `dataset.bound` стояла только здесь, а
 * хелпер `on()` в sidebarBindings её не ставил: первый вешал слушателя молча,
 * второй видел «не привязано» и вешал свой. Двадцать два контрола слушались
 * дважды — «Скриншот» делал два снимка разными путями, «Запись» стартовала и
 * тут же переключалась, а каждый ползунок писал параметр по двум несовместимым
 * адресам: `cr.setParams({ssao:{radius}})` из сайдбара и
 * `cinematic.setParameter('ssaoRadius')` отсюда.
 *
 * ПОЧЕМУ ХОЗЯИНОМ ОСТАЛСЯ САЙДБАР. Он знает 26 ползунков против 17 здешних
 * (SSR, объёмный туман, LUT, боке — только там) и целится в СОБСТВЕННУЮ канву
 * рендерера. Здешний MediaCaptureEngine брал `viewport-container
 * .querySelector('canvas')` — ПЕРВУЮ канву в документе, то есть канву
 * Cornerstone, тогда как кинематика рисует на своей, вставленной следом
 * (cinematicRendering.js:539). Снимок выходил без кинематических эффектов и
 * подписывался «Скриншот высокого разрешения сохранён».
 *
 * ЗАОДНО ПОХОРОНЕНЫ ЧЕТЫРЕ СИРОТЫ. `cine-toggle-btn`, `btn-cinematic-toggle`,
 * `btn-cinematic-left`, `btn-fit-view` — ни одного из них нет в разметке.
 * Первый удалён ещё в 10.19.19, остальных не было никогда. Сторож их не
 * замечал по двум причинам сразу, обе закрыты в uiwiring_test [11.0.2]: он не
 * знал вызовов `UIBinder.bindClick`, а три имени «спасал» массив подписей в
 * _syncUI, где они лежали как `id: '…'` — и правило «код сам создаёт элемент»
 * принимало запись в объекте за создание.
 *
 * Живого в этом наборе не было ничего: всё либо дубль, либо сирота.
 * `_handleLaunchShortcut` (ярлыки установленного приложения) — настоящая
 * работа модуля, она ниже и осталась нетронутой.
 */

// ══════════════════════════════════════════════════════════════
// PUBLIC API
// ══════════════════════════════════════════════════════════════
export function initIndexBindings() {
  // [FIX] Cleanup previous listeners
  if (_ac) _ac.abort();
  _ac = new AbortController();
  // [11.0.2] Три подписки на bindAllControllers сняты вместе с ним самим:
  // звать было нечего. Контролы кинематики ведёт sidebarBindings — один раз,
  // из initUI(), без гонки событий.

  // [10.0.3] Ярлыки установленного приложения.
  //
  // В манифесте два ярлыка — «Открыть снимки» и «Архив». Они ведут на ./ с
  // пометкой в адресе; без этой обработки нажатие на ярлык просто открывало бы
  // программу как обычно, и ярлык был бы обманом. Раньше в манифесте стояли
  // адреса /import и /recent — страниц с такими адресами не существует вовсе,
  // и ярлык вёл на 404.
  _handleLaunchShortcut();
  _привязатьВыход();
}

/**
 * [11.1.14] «Выход» в меню «Файл».
 *
 * Настольная оболочка закрывается через мост MikhmelDesktop.closeWindow.
 * В браузере window.close() разрешён только для вкладок, открытых скриптом,
 * — и когда он не срабатывает, кнопка обязана сказать это вслух, а не
 * молчать: молчащая кнопка читается как сломанная (правило nolying_test).
 */
function _привязатьВыход() {
  const кн = document.getElementById('btn-exit-app');
  if (!кн) { log.debug('[Выход] кнопки нет в разметке'); return; }
  кн.addEventListener('click', () => {
    const настольная = typeof window !== 'undefined' && window.MikhmelDesktop?.isDesktop;
    if (настольная && typeof window.MikhmelDesktop.closeWindow === 'function') {
      window.MikhmelDesktop.closeWindow();
      return;
    }
    try { window.close(); } catch (e) { /* закрытие может быть запрещено */ }
    // Дошли сюда — вкладку закрыть не дали. Говорим правду.
    setTimeout(() => {
      if (!document.hidden) {
        showToast('Браузер не разрешает программе закрыть свою вкладку — закройте её сами (Ctrl+W). В настольной версии кнопка закрывает окно.', 'info', 6000);
      }
    }, 300);
  }, { signal: _ac.signal });
  log.debug('[Выход] кнопка готова');
}

function _handleLaunchShortcut() {
  let action = '';
  try { action = new URLSearchParams(window.location.search).get('action') || ''; }
  catch (e) { return; }
  if (!action) return;

  const run = () => {
    try {
      if (action === 'import') {
        (document.getElementById('file-input') || document.getElementById('btn-import'))?.click();
      } else if (action === 'archive') {
        document.getElementById('btn-archive')?.click();
      }
    } catch (e) { /* ярлык не должен ронять запуск */ }
  };
  // Ждём готовности интерфейса: до неё кнопок ещё нет.
  window.addEventListener('mikhmel-ready', () => setTimeout(run, 400), { once: true, signal: _ac.signal });
}
// ══════════════════════════════════════════════════════════════
// ENTERPRISE FEATURES (v6) — Real functionality
// ══════════════════════════════════════════════════════════════

/**
 * Cinematic A/B quick-compare: snapshot current look, change preset, toggle back
 */
let _cinematicSnapshotA = null;
export function cinematicSnapshotA() {
  const cr = cinematic.engine;
  if (!cr) { showToast('⚠️ Кинематографический рендеринг не активен', 'warning'); return; }
  _cinematicSnapshotA = cr.getParams ? JSON.parse(JSON.stringify(cr.getParams())) : null;
  showToast('🅰 Снапшот A сохранён', 'success', 1000);
}
export function cinematicRestoreA() {
  if (!_cinematicSnapshotA) { showToast('⚠️ Нет снапшота A', 'warning'); return; }
  const cr = cinematic.engine;
  if (cr?.setParams) cr.setParams(_cinematicSnapshotA);
  showToast('🅰 Восстановлен снапшот A', 'info', 1000);
}

/**
 * Auto-rotate 3D with configurable speed and axis
 * @param {number} [degreesPerSec=30]
 */
let _autoRotateRAF = null;
export function startCinematicAutoRotate(degreesPerSec = 30) {
  if (_autoRotateRAF) return;
  let lastTime = performance.now();
  const tick = () => {
    const now = performance.now();
    const dt = (now - lastTime) / 1000;
    lastTime = now;
    cinematic.setParameter('rotation', (cinematic.engine?.params?.rotation || 0) + degreesPerSec * dt);
    // Also try to rotate via viewport camera
    try {
      const re = window.MikhmelViewer?.getRenderingEngine?.();
      if (re) {
        for (const vp of re.getViewports()) {
          if (vp.type === 2 || vp.id?.includes('3d') || vp.id?.includes('volume')) {
            const cam = vp.getCamera();
            if (cam.viewUp && cam.viewPlaneNormal) {
              const angle = degreesPerSec * dt * Math.PI / 180;
              const up = cam.viewUp, vpn = cam.viewPlaneNormal;
              const cos = Math.cos(angle), sin = Math.sin(angle);
              const cross = [up[1]*vpn[2]-up[2]*vpn[1], up[2]*vpn[0]-up[0]*vpn[2], up[0]*vpn[1]-up[1]*vpn[0]];
              const dot = up[0]*vpn[0]+up[1]*vpn[1]+up[2]*vpn[2];
              vp.setCamera({ viewUp: [up[0]*cos+cross[0]*sin+vpn[0]*dot*(1-cos), up[1]*cos+cross[1]*sin+vpn[1]*dot*(1-cos), up[2]*cos+cross[2]*sin+vpn[2]*dot*(1-cos)] });
              vp.render();
            }
            break;
          }
        }
      }
    } catch (e) { log.debug("[indexBindings]", e.message); }
    _autoRotateRAF = requestAnimationFrame(tick);
  };
  _autoRotateRAF = requestAnimationFrame(tick);
  showToast(`🔄 Автовращение ${degreesPerSec}°/s`, 'info', 1200);
}
export function stopCinematicAutoRotate() {
  if (_autoRotateRAF) { cancelAnimationFrame(_autoRotateRAF); _autoRotateRAF = null; }
  showToast('⏹ Автовращение стоп', 'info', 800);
}
export function isAutoRotating() { return !!_autoRotateRAF; }

/**
 * Get cinematic controller state
 */
export function getCinematicState() {
  return { isActive: cinematic.isActive, isBound: cinematic.isBound, hasEngine: !!cinematic.engine, autoRotating: !!_autoRotateRAF, hasSnapshotA: !!_cinematicSnapshotA };
}
export function cinematicToggle() { cinematic.toggle(); }
export function cinematicSetPreset(p) { cinematic.setPreset(p); }
// [11.0.2] Три экспорта захвата УДАЛЕНЫ: takeHRScreenshot, toggleVideoRecording,
// isVideoRecording. Ни один не звался снаружи (проверено поиском по src/ и
// tests/ — ноль совпадений), но `toggleVideoRecording` была миной замедленного
// действия: она целилась в КНОПКУ САЙДБАРА по имени 'cine-record-btn' и вела
// запись через MediaCaptureEngine — то есть с ЧУЖОЙ канвы. Сайдбар пишет
// собственную канву рендерера; сработай эти два пути вместе, вышло бы две
// записи, из которых одна без кинематических эффектов.
//
// Хозяин захвата один — sidebarBindings (решение волны 1). Здесь его больше
// нет ни строкой.


/** [FIX] Очистка глобальных слушателей */
export function destroyIndexBindings() {
  if (_ac) { _ac.abort(); _ac = null; }
}
